import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { CopyField } from "./copy-field";
import { ForwardingCodeNotice } from "./forwarding-code-notice";
import { GoogleMark, MicrosoftMark } from "./provider-marks";
import type { ForwardingCheck } from "./types";

export const EmailForwardingInstructions = ({
  address,
  check,
  embedded = false,
}: {
  address: string | null;
  check: ForwardingCheck;
  embedded?: boolean;
}) => (
  <Card
    className={cn("w-full gap-0 py-0", {
      "border-0 shadow-none": embedded,
    })}
  >
    <CardContent className={cn({ "p-5": !embedded, "p-0": embedded })}>
      <Stack className="w-full" gap={4} align="stretch">
        {!embedded && <Text variant="title">1. Set up email forwarding</Text>}
        <Text tone="muted">
          Choose your email provider. Forward invoice emails to this address and
          keep a copy in your inbox.
        </Text>
        {address ? (
          <CopyField label="Your Volca address" value={address} />
        ) : (
          <Text tone="muted">Your Volca address is not available yet.</Text>
        )}
        <details className="rounded-md border border-line p-4">
          <summary className="focus-visible:outline-auto cursor-pointer text-sm font-semibold">
            <span className="ml-2 inline-flex items-center gap-2 align-middle">
              <MicrosoftMark className="h-4 w-4 shrink-0" />
              Outlook or Microsoft 365
            </span>
          </summary>
          <Stack gap={2} align="stretch" className="w-full pt-3">
            <ol className="list-decimal space-y-2 pl-5 text-sm text-subtle">
              <li>Open Settings, then Mail, Rules, and Add new rule.</li>
              <li>
                Name the rule. Add Has attachment and the email addresses of the
                vendors whose invoices you want to forward.
              </li>
              <li>
                Add the Forward to action and enter your Volca address shown
                above.
              </li>
              <li>
                Save the rule and check that it is enabled. Keep your own copy
                of the messages.
              </li>
            </ol>
            <Text tone="muted">
              Outlook does not use a verification code. If your organization
              blocks external forwarding, ask your email administrator to allow
              it for this mailbox.
            </Text>
            <a
              className="text-sm text-link underline"
              href="https://support.microsoft.com/en-us/outlook/mail/use-rules-to-automatically-forward-messages"
              target="_blank"
              rel="noopener noreferrer"
            >
              Microsoft forwarding instructions
            </a>
          </Stack>
        </details>
        <details className="rounded-md border border-line p-4">
          <summary className="focus-visible:outline-auto cursor-pointer text-sm font-semibold">
            <span className="ml-2 inline-flex items-center gap-2 align-middle">
              <GoogleMark className="h-4 w-4 shrink-0" />
              Gmail or Google Workspace
            </span>
          </summary>
          <Stack gap={2} align="stretch" className="w-full pt-3">
            <ol className="list-decimal space-y-2 pl-5 text-sm text-subtle">
              <li>
                Open Settings, See all settings, then Forwarding and POP/IMAP.
              </li>
              <li>
                Select Add a forwarding address and enter your Volca address
                shown above.
              </li>
              <li>
                Google sends a verification code to Volca. When the code appears
                here, enter it in Gmail and select Verify.
              </li>
              <li>
                Create a filter for your invoice vendors with attachments.
                Select Forward it to, choose your Volca address, and save the
                filter. Keep a copy in your inbox.
              </li>
            </ol>
            <ForwardingCodeNotice check={check} />
            <Text tone="muted">
              The Google code permits forwarding. It does not test your rule. If
              the code does not appear, check the destination address and
              request it again in Gmail. Contact Volca if it still does not
              appear.
            </Text>
            <a
              className="text-sm text-link underline"
              href="https://support.google.com/mail/answer/10957"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google forwarding instructions
            </a>
          </Stack>
        </details>
      </Stack>
    </CardContent>
  </Card>
);
